import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  inject
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DEFAULT_PICKER_COLOR } from '~backoffice/app/~shared/constants/global.constants';
import { AttachmentType } from '~core/models/core.models';
import { ProgramBannerStateFormWithData } from '~backoffice/app/~shared/models/store.models';
import { GlobalFacade } from '~backoffice/app/~store/facades/global.facade';
import { ProgramBannerFacade } from '~backoffice/app/~store/facades/program-banner.facade';
import { tap } from 'rxjs';
import { Program } from '~core/models/program.models';

@Component({
  standalone: false,
  selector: 'edit-banner-form',
  templateUrl: './edit-banner-form.component.html',
  styleUrls: ['./edit-banner-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditBannerFormComponent {
  @Input() program: Program | null | undefined;
  programBannerFacade = inject(ProgramBannerFacade);
  globalFacade = inject(GlobalFacade);
  cdRef = inject(ChangeDetectorRef);

  attachmentType = AttachmentType;

  selectedBanner$ = this.programBannerFacade.selectedBanner$.pipe(
    tap({
      next: selectedBanner => {
        this.initForm(selectedBanner);
      }
    })
  );

  selectedBannerPropertyPath$ =
    this.programBannerFacade.selectedBannerPropertyPath$;

  canUserInteract$ = this.programBannerFacade.canUserInteract$;

  form = new FormGroup({
    title: new FormControl<string>('', [
      Validators.required,
      Validators.minLength(3)
    ]),
    titleColor: new FormControl<string>(''),
    description: new FormControl<string>('', [
      Validators.required,
      Validators.minLength(3)
    ]),
    descriptionColor: new FormControl<string>('')
  });

  violations$ = this.globalFacade.formViolations$.pipe(
    tap({
      next: violations => {
        setTimeout(() => {
          violations?.forEach(violation => {
            if (
              violation.propertyPath.includes('relatedObject') ||
              violation.propertyPath.includes('[relatedObject]')
            ) {
              this.form.get('relatedObjectIdentifier')?.setErrors({
                httpError: violation.title
              });
              return;
            }

            if (
              violation.propertyPath.includes('metadata') ||
              violation.propertyPath.includes('metadata[type]')
            ) {
              this.form.get('description')?.setErrors({
                httpError: violation.title
              });
              return;
            }

            this.form.setErrors({
              httpError: violation.title
            });
          });

          this.cdRef.detectChanges();
        }, 500);
      }
    })
  );

  colors = [DEFAULT_PICKER_COLOR, '#067bfe', '#393a40', '#ffcb3b', '#ff8800'];
  defaultColor = DEFAULT_PICKER_COLOR;

  initForm(selectedBanner?: ProgramBannerStateFormWithData): void {
    if (this.program) {
      this.form.patchValue({
        title:
          selectedBanner?.form.model?.title ||
          selectedBanner?.data.title?.value ||
          '',
        titleColor:
          selectedBanner?.form.model?.titleColor ||
          selectedBanner?.data.title?.color ||
          '',
        description:
          selectedBanner?.form.model?.description ||
          selectedBanner?.data.description?.value ||
          '',
        descriptionColor:
          selectedBanner?.form.model?.descriptionColor ||
          selectedBanner?.data.description?.color ||
          ''
      });

      this.defaultColor = this.form.value.titleColor || DEFAULT_PICKER_COLOR;
      this.cdRef.detectChanges();
    }
  }

  removeBanner(): void {
    this.programBannerFacade.removeBanner();
  }

  setColor(color: string): void {
    this.form.patchValue({ titleColor: color });
  }
}
